import { useCallback } from 'react';

export const useOrderOperations = () => {
  const parseNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const parseQty = (value) => {
    const parsed = parseInt(value);
    return isNaN(parsed) || parsed <= 0 ? 1 : parsed;
  };

  // Build size-wise breakdown for an order (orderDetail locked or lotDetails approx)
  const calculateOrderBreakdown = useCallback((order) => {
    const isPlane = (order.specification || "").toUpperCase() === "PLANE";
    const showSpecWt = !isPlane;
    const locked = !!(order.orderDetail && Object.keys(order.orderDetail).length > 0);

    // Using `purity` from order data instead of buyerDeskCalculation
    const wastage =
      parseNumber(order.purity) +
      parseNumber(order.wastage) +
      parseNumber(order.specificationMC);

    const stampCostPerUnit = parseNumber(order.stampCertificateCost);
    let rows = [];

    if (locked) {
      const detail = order.orderDetail || {};
      rows = Object.entries(detail).map(([size, d]) => {
        const qty = parseNumber(d.qty ?? d.set ?? 1);
        const gross = parseNumber(d.grossWT ?? d.grossWt) * qty;
        const net = parseNumber(d.netwt ?? d.netWt) * qty;
        const spec = parseNumber(d.specWT ?? d.specWt) * qty;
        const fine = (net * wastage) / 100;
        const stampQty = parseNumber(d.stampCertificateQtyPerSet ?? order.stampCertificateQtyPerSet ?? 1);
        const stampingCost = stampQty * qty * stampCostPerUnit;
        return {
          size,
          qty,
          gross: parseFloat(gross.toFixed(3)),
          net: parseFloat(net.toFixed(3)),
          spec: parseFloat(spec.toFixed(3)),
          fine: parseFloat(fine.toFixed(3)),
          stampQty,
          stampingCost: parseFloat(stampingCost.toFixed(2)),
          approx: false
        };
      });
    } else if (Array.isArray(order.lotDetails) && Array.isArray(order.selectedVariants)) {
      rows = order.selectedVariants
        .map((variant) => {
          const lot = order.lotDetails.find((l) => l.size === variant.size);
          if (!lot) return null;
          const qty = parseNumber(variant.quantity);
          const gross = parseNumber(lot.avgGrossWt) * qty;
          const net = parseNumber(lot.avgNetWt) * qty;
          const spec = parseNumber(lot.avgSpecWt) * qty;
          const fine = (net * wastage) / 100;
          const stampQty = parseNumber(order.stampCertificateQtyPerSet ?? 1);
          const stampingCost = stampQty * qty * stampCostPerUnit;
          return {
            size: variant.size,
            qty,
            gross: parseFloat(gross.toFixed(3)),
            net: parseFloat(net.toFixed(3)),
            spec: parseFloat(spec.toFixed(3)),
            fine: parseFloat(fine.toFixed(3)),
            stampQty,
            stampingCost: parseFloat(stampingCost.toFixed(2)),
            approx: true
          };
        })
        .filter(Boolean);
    }

    const totals = rows.reduce(
      (acc, r) => {
        acc.totalGross += r.gross;
        acc.totalNet += r.net;
        acc.totalSpec += r.spec;
        acc.totalFine += r.fine;
        acc.totalQty += r.qty;
        acc.totalStampQty += r.stampQty * r.qty;
        acc.totalStampingCost += r.stampingCost;
        return acc;
      },
      { totalGross: 0, totalNet: 0, totalSpec: 0, totalFine: 0, totalQty: 0, totalStampQty: 0, totalStampingCost: 0 }
    );

    return {
      rows,
      totals,
      locked,
      showSpecWt,
      hasSizes: rows.length > 0,
      approx: !locked,
      stampCostPerUnit,
      wastagePercent: wastage
    };
  }, []);

  // Compute every metric needed for an order
  const calculateOrderMetrics = useCallback((order) => {

    const wastage =
      parseNumber(order.purity) +
      parseNumber(order.wastage) +
      parseNumber(order.specificationMC);

    const setMc = parseNumber(order.setMc || order.setMakingCharge);
    const gramMcPerGram = parseNumber(order.netGramMc || order.gramMc || order.makingChargePerGram);
    const specMcPerGram = parseNumber(order.specificationGramRate || order.specMcPerGram || order.specGramRate);

    const breakdown = calculateOrderBreakdown(order);

    let effectiveNetWt = 0;
    let effectiveGrossWt = 0;
    let effectiveSpecWt = 0;
    let totalSet = 0;
    let stampCostTotal = 0;

    if (breakdown.hasSizes) {
      effectiveGrossWt = breakdown.totals.totalGross;
      effectiveNetWt = breakdown.totals.totalNet;
      effectiveSpecWt = breakdown.totals.totalSpec;
      totalSet = breakdown.totals.totalQty;
      stampCostTotal = breakdown.totals.totalStampingCost;
    } else {
      const isLotBased = Array.isArray(order.lotDetails) && order.lotDetails.length > 0;
      if (isLotBased) {
        effectiveGrossWt = order.lotDetails.reduce((s, l) => {
          const variant = (order.selectedVariants || []).find((v) => v.size === l.size);
          const qty = parseNumber(variant?.quantity) || parseNumber(l.set) || 1;
          return s + parseNumber(l.avgGrossWt) * qty;
        }, 0);
        effectiveNetWt = order.lotDetails.reduce((s, l) => {
          const variant = (order.selectedVariants || []).find((v) => v.size === l.size);
          const qty = parseNumber(variant?.quantity) || parseNumber(l.set) || 1;
          return s + parseNumber(l.avgNetWt) * qty;
        }, 0);
        effectiveSpecWt = order.lotDetails.reduce((s, l) => {
          const variant = (order.selectedVariants || []).find((v) => v.size === l.size);
          const qty = parseNumber(variant?.quantity) || parseNumber(l.set) || 1;
          return s + parseNumber(l.avgSpecWt) * qty;
        }, 0);
        totalSet = (order.selectedVariants || []).reduce((sum, v) => sum + parseNumber(v.quantity), 0) || 1;
      } else {
        effectiveNetWt = parseNumber(order.netWt || order.displayNetWt || order.totalNetWt);
        effectiveGrossWt = parseNumber(order.grossWt || order.displayGrossWt || order.totalGrossWt) || effectiveNetWt;
        effectiveSpecWt = parseNumber(order.specificationWt || order.specWt) || effectiveGrossWt;
        totalSet = parseQty(order.orderPiece || order.quantity || order.orderQuantity || order.totalPieces);
      }
      const stampCostPerUnit = parseNumber(order.stampCertificateCost);
      const stampQtyPerSet = parseQty(order.stampCertificateQtyPerSet);
      stampCostTotal = stampCostPerUnit * stampQtyPerSet * totalSet;
    }

    const fineWeight = (effectiveNetWt * wastage) / 100;
    const mcAmount = parseNumber(order.totalMc || order.totalMC || order.makingCharges) || (setMc * totalSet + gramMcPerGram * effectiveNetWt + specMcPerGram * effectiveSpecWt);
    const totalAmount = mcAmount + stampCostTotal;

    return {
      grossWt: parseFloat(effectiveGrossWt.toFixed(3)),
      netWt: parseFloat(effectiveNetWt.toFixed(3)),
      specWt: parseFloat(effectiveSpecWt.toFixed(3)),
      fineWeight: parseFloat(fineWeight.toFixed(3)),
      totalMc: parseFloat(mcAmount.toFixed(2)),
      stampCost: parseFloat(stampCostTotal.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      totalSets: totalSet,
      approx: breakdown.approx,
      wastagePercent: wastage
    };
  }, [calculateOrderBreakdown]);

  const calculateDispatchSummary = useCallback((group) => {
    let totalGross = 0;
    let totalNet = 0;
    let totalSpec = 0;
    let totalFine = 0;
    let totalMc = 0;
    let totalStamp = 0;
    let totalAmount = 0;
    let totalSets = 0;

    group.orders.forEach((order) => {
      const m = calculateOrderMetrics(order);
      totalGross += m.grossWt;
      totalNet += m.netWt;
      totalSpec += m.specWt;
      totalFine += m.fineWeight;
      totalMc += m.totalMc;
      totalStamp += m.stampCost;
      totalAmount += m.totalAmount;
      totalSets += m.totalSets;
    });

    return {
      totalItems: group.totalItems,
      totalGrossWeight: parseFloat(totalGross.toFixed(3)),
      totalNetWeight: parseFloat(totalNet.toFixed(3)),
      totalSpecWeight: parseFloat(totalSpec.toFixed(3)),
      totalFineWeight: parseFloat(totalFine.toFixed(3)),
      totalMc: parseFloat(totalMc.toFixed(2)),
      totalStampCost: parseFloat(totalStamp.toFixed(2)),
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      totalSets: parseFloat(totalSets.toFixed(0)),
      totalPackets: group.orders.length,
    };
  }, [calculateOrderMetrics]);

  return {
    calculateOrderMetrics,
    calculateOrderBreakdown,
    calculateDispatchSummary,
  };
};